import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import useEnrolPageProps from "@hooks/useEnrolPageProps";
import usePostCheckoutState from "@hooks/usePostCheckoutState";

import useAppStateSync from "@/hooks/useAppStateSync";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { dispatchGAEvents } from "@/utils/events/dispatchGAEvents";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "jotai";

import { Button, Divider, Flex, Spacer } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { AddonInterface } from "@ts/interfaces/Checkout/AddonInterface";
import { CheckoutInterface } from "@ts/interfaces/Checkout/CheckoutInterface";
import { PlanInterface } from "@ts/interfaces/Checkout/PlanInterface";

import {
    checkoutAtom,
    isNewsletterSubEnabledAtom,
    pageQueryParamsAtom,
    parentIdAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom.ts";

import { dispatchHSEvents } from "@utils/events/dispatchHSEevents";
import {
    getPlansForTargetGrade,
    getSubjectsByGrade,
    getTrackingId
} from "@utils/utils";

import ContentLayout from "@layouts/ContentLayout";

import { Card } from "@components/Card";
import { Feature } from "@components/Feature";
import { Skeleton } from "@components/Skeleton";
import { Text } from "@components/Text";

const MembershipContainer = () => {
    const {
        state: { checkout: checkoutAppState }
    } = useAppStateSync();

    const { hubspotEventCheckout, subjectsByGrade, parentUser } =
        useCheckoutPageProps();
    const { hubspotEventEnrol } = useEnrolPageProps();

    const { t } = useTranslation();

    const navigate = useNavigate();

    const [plans, setPlans] = useState<PlanInterface[]>([]);
    const [selectMembershipDescription, setSelectMembershipDescription] =
        useState<string | undefined>();

    const checkoutContext = useContext(CheckoutContext);

    const { userSelectedCountry, userSelectedLang, productFamilies, isProductFamiliesLoading } =
        checkoutContext;

    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);
    const [isNewsletterSubEnabled] = useAtom(isNewsletterSubEnabledAtom);

    const [parentId] = useAtom(parentIdAtom);

    const [learnMore, setLearnMore] = useState<string[]>([]);
    const subjects = getSubjectsByGrade(
        subjectsByGrade,
        checkout.grade?.id as number
    );
    const isParentUserLoggedIn = !!parentUser;

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();
    const { checkoutStateMutationState, checkoutStateMutate } =
        usePostCheckoutState(() => {
            /**
             * Some plans may not have associated addons, and some plans may have addons
             * with a price of 0 (which are included by default). Therefore, users won't be selecting these addons, so the
             * addon selection page is skipped in such scenario.
             */
            if (
                checkout.plan?.addons &&
                // logical AND is short-circuiting
                checkout.plan.addons.every(a => !a.isIncluded)
            ) {
                navigate(
                    routeWithQueryParams(
                        CheckoutPageRoutesEnum.ADDON_SELECT_PAGE
                    )
                );
            } else {
                navigate(
                    routeWithQueryParams(CheckoutPageRoutesEnum.PAYMENT_PAGE)
                );
            }
        });

    const isPlanSelected = !!checkout.plan;

    const onSelectPlan = (plan: PlanInterface) => {
        let defaultAddon: null | AddonInterface = null;
        const availableAddons = plan.addons;
        if (availableAddons) {
            defaultAddon = availableAddons.find(a => !a.isHighlighted) ?? null;
        }

        setCheckout((prev: CheckoutInterface | Promise<CheckoutInterface>) => ({
            ...prev,
            plan,
            addon: defaultAddon,
            nextAttemptPage:
                checkout.plan?.addons &&
                // logical AND is short-circuiting
                checkout.plan.addons.every(a => !a.isIncluded)
                    ? CheckoutPagesEnum.ADDON_SELECT_PAGE
                    : CheckoutPagesEnum.PAYMENT_PAGE
        }));
    };

    const onGoBack = () => {
        // TODO: Improve hardcorded check for contries don't need state selections
        if (
            isParentUserLoggedIn &&
            subjects &&
            subjects.length > 0 &&
            userSelectedCountry !== "VN"
        ) {
            navigate(
                routeWithQueryParams(
                    CheckoutPageRoutesEnum.GRADE_CURRICULUM_PAGE
                )
            );
        } else {
            navigate(
                routeWithQueryParams(
                    CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                )
            );
        }
    };

    const onSaveData = () => {
        dispatchHSEvents(
            "trackPageView",
            CheckoutPageRoutesEnum.PLAN_SELECT_PAGE,
            undefined,
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );
        dispatchHSEvents(
            "trackCustomBehavioralEvent",
            undefined,
            {
                checkout_status: "ABANDON",
                email: checkout.user.email,
                checkout_token: userCheckoutStateToken,
                selected_country: userSelectedCountry,
                hs_language: userSelectedLang,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no",
                checkout_indicative_plan: checkout.plan
                    ? checkout.plan.id
                    : pageQueryParams?.plan
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        dispatchGAEvents({
            event: "select_payment_options",
            paymentOptionData: {
                payment_option: `${checkout.plan!.name} - ${
                    checkout.plan!.description
                }`
            },
            parentId
        });

        checkoutStateMutate();
    };

    const handleLearnMore = (planId: string) => {
        setLearnMore(prevExpandedItems => {
            if (prevExpandedItems.includes(planId)) {
                return prevExpandedItems.filter(id => id !== planId);
            }
            return [...prevExpandedItems, planId];
        });
    };

    useEffect(() => {
        const filterPlansBySelectedGrade = () => {
            /**
             * For Senior product families, there are diffent plans will be shown
             * based on the selected grade.
             */
            if (checkout.productFamily && checkout.grade) {
                const { plans, selectMembershipDescription } =
                    checkout.productFamily;
                const selectedGrade = checkout.grade;
                setSelectMembershipDescription(selectMembershipDescription);
                setPlans(getPlansForTargetGrade(plans, selectedGrade.name));
            }
        };

        filterPlansBySelectedGrade();
    }, [checkout]);

    /**
     * "useEffect" hook to set the query parameters dynamically based on the selected product.
     */
    useEffect(() => {
        if (checkoutStateMutationState.isSuccess) {
            // Update the related query parameters with the selected plan.
            onSetupQueryParams({
                ...pageQueryParams,
                addon: checkout.addon ? checkout.addon.id : null,
                plan: checkout.plan!.id
            });
        }
        dispatchHSEvents(
            "trackCustomBehavioralEvent",
            undefined,
            {
                checkout_status: "ABANDON",
                email: checkout.user.email,
                checkout_token: userCheckoutStateToken,
                selected_country: userSelectedCountry,
                hs_language: userSelectedLang,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no",
                checkout_indicative_plan: checkout.plan
                    ? checkout.plan.id
                    : pageQueryParams?.plan
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );
    }, [checkout, checkoutStateMutationState]);

    useEffect(() => {
        if (checkoutAppState?.checkoutPurchasingAddon) {
            // TODO: Improve hardcorded check for contries don't need state selections
            navigate(
                userSelectedCountry === "VN"
                    ? CheckoutPageRoutesEnum.ADDON_SELECT_PAGE
                    : CheckoutPageRoutesEnum.STATE_SELECT_PAGE
            );
        }
        if (checkoutAppState?.checkoutSubjectsFee) {
            navigate(CheckoutPageRoutesEnum.PAYMENT_PAGE);
        }
    }, []);

    const getPlanForDataTracking = (plan: PlanInterface) => {
        if (plan.period === "month") {
            return "term";
        } else if (plan.period === "year" && plan.titlePeriod === "per year") {
            return "yearly";
        } else if (
            plan.period === "year" &&
            plan.id.search("Installments") !== -1
        ) {
            return "installments";
        } else if (
            plan.period === "year" &&
            plan.id.search("Installments") === -1 &&
            plan.titlePeriod === ""
        ) {
            return "yearly";
        }
        return "instalments";
    };

    return (
        <ContentLayout
            className={ClassesEnum.MembershipContainer}
            containerWidth={["95%", null, null, null]}
            title={t(
                "checkout.selectMembershipPage.title",
                "Which program option best suits your needs?"
            )}
            footerActionsProps={{
                canGoNext: isPlanSelected,
                isNextBtnLoading: checkoutStateMutationState.isPending,
                onGoNext: onSaveData,
                onGoBack,
                btnClassHierarchy: {
                    next: [ClassesEnum.MembershipContainer, ClassesEnum.Button],
                    back: [ClassesEnum.MembershipContainer, ClassesEnum.Button]
                },
                btnActions: {
                    next: "goNextFromPlanSelectPage",
                    back: "goBackFromPlanSelectPage"
                }
            }}
            step={3}
        >
            <Text alignSelf={[null, null, "center"]} width={[null, null, 758]}>
                {selectMembershipDescription}
            </Text>
            <Flex
                direction={["column", "column", "column", "row"]}
                gap={[6, null, 8]}
                justify="center"
                align={{
                    base: "center",
                    sm: "center",
                    md: "center",
                    lg: "stretch"
                }}
                width="100%"
            >
                {productFamilies !== null && plans.length < 1 && (
                    <Text fontSize={14} color="gray.600">
                        <Trans>No programs found for this grade</Trans>
                    </Text>
                )}
                {productFamilies !== null &&
                    plans.map(p => (
                        <Card<PlanInterface>
                            classHierarchy={[
                                ClassesEnum.MembershipContainer,
                                ClassesEnum.Card
                            ]}
                            action={`selectPlan-${getPlanForDataTracking(p)}`}
                            key={p.id}
                            isHighlighted={p.isHighlighted}
                            highlightedText={p.highlightedText}
                            highlightedTextAlign="center"
                            item={p}
                            isActive={p.id === checkout.plan?.id}
                            onClick={onSelectPlan}
                            data-testid={`plan-card-${p.id}`}
                            width={[288, null, 363]}
                            /* height={[
                                learnMore.includes(p.id) ? "100%" : 256,
                                null,
                                598
                            ]} */
                            cardBorderRadius="32px"
                            cardBorder="4px"
                        >
                            <Flex
                                margin="auto"
                                direction="column"
                                gap={4}
                                width="100%"
                                alignItems="stretch"
                            >
                                <Flex
                                    direction="column"
                                    gap={2}
                                    align="center"
                                    justifyContent="start"
                                    // height={["auto", null, 218]}
                                >
                                    <Text
                                        fontSize={14}
                                        fontWeight={600}
                                        fontFamily="inter, sans-serif"
                                        textAlign="center"
                                        color="gray.500"
                                        textTransform="uppercase"
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.MembershipContainer,
                                                ClassesEnum.Card,
                                                ClassesEnum.Text
                                            ],
                                            `planGrade-${getPlanForDataTracking(
                                                p
                                            )}`
                                        )}
                                    >
                                        {checkout.grade?.name}
                                    </Text>
                                    <Text
                                        fontSize={[32, null, 40]}
                                        type="heading"
                                        textAlign="center"
                                        color={
                                            p.id === checkout.plan?.id
                                                ? "burntYellow.500"
                                                : "gray.800"
                                        }
                                        fontFamily="komu, sans-serif"
                                    >
                                        {p.name}
                                    </Text>
                                    <Text
                                        fontSize={[14, null, 28]}
                                        color="gray.800"
                                        fontWeight={400}
                                        fontFamily="komu, sans-serif"
                                        textTransform="uppercase"
                                        textAlign="center"
                                    >
                                        {p.description}
                                    </Text>
                                    <Spacer display={["none", null, "flex"]} />
                                    <Flex
                                        align="center"
                                        direction="row"
                                        justify="center"
                                        gap={1}
                                        whiteSpace="nowrap"
                                        alignItems="baseline"
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.MembershipContainer,
                                                ClassesEnum.Card
                                            ],
                                            `priceDetails-${getPlanForDataTracking(
                                                p
                                            )}`
                                        )}
                                    >
                                        {p.crossedPriceLabel && (
                                            <Text
                                                fontSize={[16, null, 20]}
                                                color="gray.500"
                                                fontWeight={400}
                                                fontFamily="inter, sans-serif"
                                                position="relative"
                                                display="inline-block"
                                                sx={{
                                                    "&:before": {
                                                        content: "''",
                                                        position: "absolute",
                                                        left: 0,
                                                        top: "50%",
                                                        right: 0,
                                                        borderTop: "2px solid",
                                                        borderColor: "gray.500",
                                                        transform:
                                                            "rotate(-4.441deg)"
                                                    }
                                                }}
                                                data-tracking-id={getTrackingId(
                                                    [
                                                        ClassesEnum.MembershipContainer,
                                                        ClassesEnum.Card,
                                                        ClassesEnum.Text
                                                    ],
                                                    `planCrossedPrice-${getPlanForDataTracking(
                                                        p
                                                    )}`
                                                )}
                                            >
                                                {p.crossedPriceLabel}
                                            </Text>
                                        )}
                                        <Text
                                            fontSize={[20, null, 24]}
                                            type="subHeading"
                                            fontWeight={600}
                                            fontFamily="inter, sans-serif"
                                            mt={6}
                                        >
                                            {p.title}
                                        </Text>
                                        <Text
                                            fontSize={[14, null, 16]}
                                            type="subHeading"
                                            fontWeight={600}
                                            fontFamily="inter, sans-serif"
                                        >
                                            {p.titlePeriod}
                                        </Text>
                                    </Flex>
                                </Flex>
                                <Divider
                                    color="gray.300"
                                    border="1px"
                                    display={[
                                        learnMore.includes(p.id)
                                            ? "inline"
                                            : "none",
                                        null,
                                        "inline"
                                    ]}
                                />
                                <Flex
                                    direction="column"
                                    gap={2}
                                    display={[
                                        learnMore.includes(p.id)
                                            ? "flex"
                                            : "none",
                                        null,
                                        "flex"
                                    ]}
                                    justifyContent="start"
                                >
                                    {p.features.map(f => (
                                        <Feature
                                            key={f.id}
                                            isActive={f.isAvailable}
                                            text={f.text}
                                            data-tracking-id={getTrackingId(
                                                [
                                                    ClassesEnum.MembershipContainer,
                                                    ClassesEnum.Card
                                                ],
                                                `planFeature-${f.id}`
                                            )}
                                        />
                                    ))}
                                </Flex>
                                {p?.addons &&
                                    p.addons.length > 0 &&
                                    p?.addons?.filter(addon => addon.price > 0)
                                        .length > 0 && (
                                        <>
                                            <Flex
                                                alignItems="center"
                                                display={[
                                                    learnMore.includes(p.id)
                                                        ? "flex"
                                                        : "none",
                                                    null,
                                                    "flex"
                                                ]}
                                            >
                                                <Divider
                                                    color="gray.300"
                                                    border="1px"
                                                />
                                                <Text
                                                    pl={1}
                                                    pr={1}
                                                    color="gray.500"
                                                    fontSize={12}
                                                    fontFamily="inter, sans-serif"
                                                    textAlign="center"
                                                    whiteSpace="nowrap"
                                                >
                                                    <Trans>
                                                        OPTIONAL PREMIUM
                                                        SERVICES
                                                    </Trans>
                                                </Text>
                                                <Divider
                                                    color="gray.300"
                                                    border="1px"
                                                />
                                            </Flex>
                                            <Flex
                                                direction="column"
                                                gap={2}
                                                justifyContent="start"
                                                display={[
                                                    learnMore.includes(p.id)
                                                        ? "flex"
                                                        : "none",
                                                    null,
                                                    "flex"
                                                ]}
                                            >
                                                {p?.addons
                                                    .filter(
                                                        addon =>
                                                            addon.isHighlighted
                                                    )
                                                    .map(a => (
                                                        <Feature
                                                            key={a.id}
                                                            isActive={
                                                                a.isHighlighted
                                                            }
                                                            text={a.name}
                                                            data-tracking-id={getTrackingId(
                                                                [
                                                                    ClassesEnum.MembershipContainer,
                                                                    ClassesEnum.Card
                                                                ],
                                                                `planAddon-${a.id}`
                                                            )}
                                                        />
                                                    ))}
                                            </Flex>
                                        </>
                                    )}
                                {p?.addons?.filter(addon => addon.isHighlighted)
                                    .length === 0 && (
                                    <Flex
                                        direction="column"
                                        gap={2}
                                        justifyContent="start"
                                        display={[
                                            learnMore.includes(p.id)
                                                ? "flex"
                                                : "none",
                                            null,
                                            "flex"
                                        ]}
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.MembershipContainer,
                                                ClassesEnum.Card
                                            ],
                                            `planWithNoAddons-${p.id}`
                                        )}
                                    >
                                        <Feature
                                            isActive={false}
                                            text="No premium services available"
                                        />
                                    </Flex>
                                )}
                            </Flex>
                            <Spacer />
                            <Flex
                                display={["flex", "flex", "none"]}
                                cursor="pointer"
                                gap={1}
                                align="center"
                                alignSelf="center"
                            >
                                <Button
                                    color="royalBlue.500"
                                    fontFamily="ballinger, sans-serif"
                                    fontSize={14}
                                    onClick={() => handleLearnMore(p.id)}
                                    variant="unstyled"
                                    p={0}
                                    data-tracking-id={getTrackingId(
                                        [
                                            ClassesEnum.MembershipContainer,
                                            ClassesEnum.Card,
                                            ClassesEnum.Button
                                        ],
                                        `learnMore-${getPlanForDataTracking(p)}`
                                    )}
                                >
                                    {learnMore.includes(p.id)
                                        ? "Hide"
                                        : "Learn more"}
                                </Button>

                                {!learnMore.includes(p.id) && (
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        color="var(--euka-colors-royalBlue-500)"
                                        fontSize={16}
                                    />
                                )}
                                {learnMore.includes(p.id) && (
                                    <FontAwesomeIcon
                                        icon={faChevronUp}
                                        color="var(--euka-colors-royalBlue-500)"
                                        fontSize={16}
                                    />
                                )}
                            </Flex>
                            <Spacer />
                        </Card>
                    ))}
                {isProductFamiliesLoading && <Skeleton />}
            </Flex>
        </ContentLayout>
    );
};

export default MembershipContainer;
